import { Box, Checkbox, FormControlLabel } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApplicationPermissionType, ConfirmationDialogResult, UnitPermissionApplicationPermissionTypes } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { ISessionState } from "../../../../models/session/sessionState";
import { IUnitModuleNode, UnitModuleNode } from "../../../../models/unit/unitModuleNode";
import { UnitModuleNodeSave } from "../../../../models/unit/unitModuleNodeSave";
import { IUnitModuleNodeUsage, UnitModuleNodeUsage } from "../../../../models/unit/unitModuleNodeUsage";
import { CompanyUnitDialog } from "../../../company/Company/CompanyUnitDialog";
import { ActionContainer } from "../../../framework/ActionContainer";
import { ConfirmationButton } from "../../../framework/Button/ConfirmationButton";
import { DialogSettingsCard } from "../../../framework/Container/DialogSettingsCard";
import { ListIconButtonsItem } from "../../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { UnitModuleTree } from "../UnitModuleTree";
import { UnitModuleSaveResult } from "source/models/unit/unitModuleSaveResult";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

export interface IUnitModuleSettingsProp {
    classes?: string;
    infoText?: string;
    companyId: string;
    sessionState: ISessionState;
    applicationPermissionType: ApplicationPermissionType;
    unitModuleNode: IUnitModuleNode;
    onSave?: (obj: any) => Promise<UnitModuleSaveResult>;
    onOk: (closeDialog: boolean) => void;
    onCancel: (closeDialog: boolean) => void;
}

export interface IUnitModuleSettingsState {
    unitModuleNodeId: string;
    unitModuleNode: IUnitModuleNode;
    unitModuleNodes: IUnitModuleNode[];
    editUnitModuleNode: IUnitModuleNode;
    selectedUnitModuleNode: IUnitModuleNode;
    showUnitDialog: boolean;
    showUnitDialogForNew: boolean;
    saveUnitNodeIds: string[];
    removeUnitNodeIds: string[];
    showOnlyUnitsThatAreInUse: boolean;
}

export class UnitModuleSettings extends React.Component<IUnitModuleSettingsProp, IUnitModuleSettingsState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IUnitModuleSettingsProp) => {
        const unitModuleNode = new UnitModuleNode(props.unitModuleNode);
        unitModuleNode.setNotInUseNodesHidden(props.applicationPermissionType, true);
        return {
            unitModuleNodeId: props.unitModuleNode?.id ?? "",
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([], 0),
            editUnitModuleNode: null,
            selectedUnitModuleNode: unitModuleNode,
            showUnitDialog: false,
            showUnitDialogForNew: false,
            saveUnitNodeIds: [],
            removeUnitNodeIds: [],
            showOnlyUnitsThatAreInUse: true
        };
    };

    constructor(props: IUnitModuleSettingsProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    componentDidUpdate(prevProps: IUnitModuleSettingsProp, prevState: IUnitModuleSettingsState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId && state.unitModuleNodeId === (props.unitModuleNode?.id ?? "")) return;
        const newState = this.getStateFromProps(props);
        this.setState(newState);
        this.orgStateHash = this.getSaveDataFromState(props, newState).hash;
    }

    // #region Units
    handleUnitNodeClick = (id: string) => {
        if (!id) return;
        const selectedUnitModuleNode = this.state.unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        this.setState({
            selectedUnitModuleNode
        });
    };

    handleEditUnitNode = () => {
        const state = this.state;
        if (!state.selectedUnitModuleNode) return;
        this.setState({
            showUnitDialog: true,
            showUnitDialogForNew: false,
            editUnitModuleNode: state.selectedUnitModuleNode
        });
    };

    handleAddUnitNode = () => {
        const state = this.state;
        if (!state.selectedUnitModuleNode) return;
        const editUnitModuleNode = new UnitModuleNode();
        editUnitModuleNode.id = Base.getGuid();
        editUnitModuleNode.parent = state.selectedUnitModuleNode;
        editUnitModuleNode.usages = state.selectedUnitModuleNode.usages.slice(0);
        for (const usage of editUnitModuleNode.usages) {
            if (UnitPermissionApplicationPermissionTypes.indexOf(usage.applicationPermissionType) > -1) continue;
            usage.enabled = false;
        }
        this.setState({
            showUnitDialog: true,
            showUnitDialogForNew: true,
            editUnitModuleNode
        });
    };

    handleSetUsage = (id: string, applicationPermissionType: ApplicationPermissionType, inUse: boolean) => {
        const state = this.state;
        if (!id) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        const usage = new UnitModuleNodeUsage();
        usage.applicationPermissionType = applicationPermissionType;
        usage.inUse = inUse;
        const modifiedUnitIds = selectedUnitModuleNode.setUsages([usage]);
        this.setState({
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([]),
            saveUnitNodeIds: state.saveUnitNodeIds.filter(i => modifiedUnitIds.indexOf(i) < 0).concat(modifiedUnitIds)
        });
    };

    handleSetCollapsed = (id: string, value: boolean) => {
        const state = this.state;
        if (!id) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        selectedUnitModuleNode.collapsed = value;
        this.setState({
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([])
        });
    };

    handleCompanyUnitDialogOk = (name: string, comment: string, usages: IUnitModuleNodeUsage[]) => {
        const state = this.state;
        if (!state.editUnitModuleNode) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        if (state.showUnitDialogForNew) {
            const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.id);
            if (!selectedUnitModuleNode) return;
            state.editUnitModuleNode.name = name;
            selectedUnitModuleNode.addUnitModuleNode(state.editUnitModuleNode);
        }
        const editUnitNode = unitModuleNode.getUnitModuleNodeById(state.editUnitModuleNode.id);
        if (!editUnitNode) return;
        editUnitNode.name = name;
        editUnitNode.comment = comment;
        let modifiedUnitIds: string[] = [];
        if (!editUnitNode.getIsRoot()) {
            modifiedUnitIds = editUnitNode.setUsages(usages);
        }
        this.setState({
            showUnitDialog: false,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([]),
            selectedUnitModuleNode: editUnitNode,
            saveUnitNodeIds: state.saveUnitNodeIds.filter(i => modifiedUnitIds.indexOf(i) < 0).concat(modifiedUnitIds)
        });
    };

    canRemoveUnitNode = (unitModuleNode: IUnitModuleNode): boolean => {
        return unitModuleNode && !unitModuleNode.getIsRoot() && !unitModuleNode.getHasChildren();
    };

    handleRemoveUnitNode = async () => {
        const state = this.state;
        if (!this.canRemoveUnitNode(state.selectedUnitModuleNode)) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveUnitParameter, state.selectedUnitModuleNode.name)) === ConfirmationDialogResult.Yes) {
            let removeUnitNodeIds = state.removeUnitNodeIds.slice(0);
            const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
            const newRemoveUnitNodeIds = unitModuleNode.removeUnitModuleNodeById(state.selectedUnitModuleNode.id);
            if (!state.selectedUnitModuleNode.isNew()) {
                removeUnitNodeIds = removeUnitNodeIds.concat(newRemoveUnitNodeIds);
            }
            const selectedUnitModuleNode = state.selectedUnitModuleNode ? unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.parent.id) : null;
            this.setState({
                unitModuleNode,
                unitModuleNodes: unitModuleNode.getAsList([]),
                removeUnitNodeIds,
                saveUnitNodeIds: state.saveUnitNodeIds.filter(i => newRemoveUnitNodeIds.indexOf(i) < 0),
                selectedUnitModuleNode: selectedUnitModuleNode ?? unitModuleNode
            });
        }
    };

    handleCompanyUnitDialogCancel = () => {
        this.setState({
            showUnitDialog: false
        });
    };

    setShowOnlyUnitsThatAreInUse = (value: boolean) => {
        const state = this.state;
        const unitModuleNode = new UnitModuleNode(this.state.unitModuleNode);
        unitModuleNode.setNotInUseNodesHidden(this.props.applicationPermissionType, value);
        const selectedUnitModuleNode = state.selectedUnitModuleNode ? unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.id) : null;
        this.setState({
            showOnlyUnitsThatAreInUse: value,
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([])
        });
    };
    // #endregion Units

    getSaveDataFromState = (props: IUnitModuleSettingsProp, state: IUnitModuleSettingsState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        // units
        const saveUnitNodeIds = state.saveUnitNodeIds.slice(0);
        for (const unitModuleNode of state.unitModuleNodes) {
            if (unitModuleNode.getIsRoot()) continue;
            let modified = false;
            for (const usage of unitModuleNode.usages) {
                if (usage.visible) continue;
                if (!usage.inUse) continue;
                usage.inUse = false;
                modified = true;
            }
            if (modified && saveUnitNodeIds.indexOf(unitModuleNode.id) > -1) {
                saveUnitNodeIds.push(unitModuleNode.id);
            }
        }
        result.append("unitModuleNodeSaves", state.unitModuleNodes.reduce((result, unitModuleNode) => {
            if (saveUnitNodeIds.indexOf(unitModuleNode.id) > -1) {
                result.push(new UnitModuleNodeSave(unitModuleNode));
            }
            return result;
        }, []));
        result.append("removeUnitIds", state.removeUnitNodeIds);
        return result;
    };

    saveToDb = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => props.onSave(saveData.obj));
        if (!result) return;
        //Update rowIds after save since the window stays open in some scenarios
        const unitModuleNode = new UnitModuleNode(this.state.unitModuleNode);
        for (const idRowId of result.rowIds) {
            const node = unitModuleNode.getUnitModuleNodeById(idRowId.id);
            if (!node) continue;
            node.rowId = idRowId.rowId;
        }
        const selectedUnitModuleNode = state.selectedUnitModuleNode
            ? unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.id)
            : null;
        const stateChange = {
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([]),
            saveUnitNodeIds: [],
            removeUnitNodeIds: []
        };
        const newState = { ...this.state, ...stateChange };
        // Save new hash since the window stays open in some scenarios
        this.orgStateHash = this.getSaveDataFromState(props, newState).hash;
        this.setState(stateChange, () => props.onOk(closeDialog));
    };

    handleSave = () => {
        this.saveToDb(true);
    };

    handleCancel = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await this.saveToDb(closeDialog);
        } else {
            props.onCancel(closeDialog);
        }

        return true;
    };

    render() {
        const props = this.props;
        const state = this.state;
        const selectedUnitId = state.selectedUnitModuleNode?.id;
        return (
            <Box display="flex" flexDirection="column" flexGrow={2}>
                <Box display="flex" flexDirection="column" paddingBottom="1.9rem" flexGrow="1">
                    <DialogSettingsCard
                        fitToContainerHeight
                        title={Translations.Units}
                        info={props.infoText}
                        cardContent={
                            <ActionContainer
                                leftItems={
                                    <FormControlLabel label={Translations.ShowOnlyUnitsThatAreInUse} control={
                                        <Checkbox
                                            checked={state.showOnlyUnitsThatAreInUse}
                                            onChange={(_e, v) => this.setShowOnlyUnitsThatAreInUse(v)}
                                        />
                                    }
                                    />
                                }
                                rightItems={
                                    <ListIconButtonsItem
                                        addTooltip={Translations.AddUnit}
                                        editTooltip={Translations.EditUnit}
                                        removeTooltip={Translations.RemoveUnit}
                                        editDisabled={!selectedUnitId}
                                        removeDisabled={!this.canRemoveUnitNode(state.selectedUnitModuleNode)}
                                        onAdd={this.handleAddUnitNode}
                                        onEdit={this.handleEditUnitNode}
                                        onRemove={this.handleRemoveUnitNode}
                                    />
                                }
                            />
                        }
                        content={
                            <UnitModuleTree
                                sessionState={props.sessionState}
                                unitModuleNodes={state.unitModuleNodes}
                                selectedId={selectedUnitId}
                                isFirstNode={true}
                                oneModuleUnitsMode={true}
                                canEditWhistleBlowSettings={true}
                                onClick={this.handleUnitNodeClick}
                                onDoubleClick={this.handleEditUnitNode}
                                onSetUsage={this.handleSetUsage}
                                onSetCollapsed={this.handleSetCollapsed}
                            />
                        }
                    />
                    {state.showUnitDialog &&
                        <CompanyUnitDialog
                            sessionState={props.sessionState}
                            unitModuleNode={state.editUnitModuleNode}
                            canEditWhistleBlowSettings={true}
                            isNew={state.showUnitDialogForNew}
                            onOk={this.handleCompanyUnitDialogOk}
                            onCancel={this.handleCompanyUnitDialogCancel}
                        />
                    }
                </Box>
                <Box display="flex" justifyContent="flex-end">
                    <AppButton variant="outlined" sx={{ marginRight: ".5rem" }} onClick={() => this.handleCancel(true)}>{Translations.Close}</AppButton>
                    <ConfirmationButton onClick={this.handleSave} disabled={this.getSaveDataFromState(props, state).hash === this.orgStateHash}>{Translations.Save}</ConfirmationButton>
                </Box>
            </Box>
        );
    }
}